import React from 'react';
import { motion } from 'motion/react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { FileText, Shield, CreditCard, UserX, AlertCircle, CheckCircle, Building2, Scale, Lock } from 'lucide-react';

const TermsConditions = () => {
  const springConfig = {
    type: "spring",
    damping: 80,
    stiffness: 400,
    mass: 1
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-orange-50 to-pink-50">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springConfig}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 
              className="text-5xl md:text-6xl font-bold mb-6"
              style={{ color: '#0A0A0A' }}
            >
              Terms & Conditions
            </h1>
            <p className="text-xl text-gray-600 mb-4">
              Please read these terms carefully before using Typeit 4U.
            </p>
            <p className="text-sm text-gray-500 mb-6">Last Updated: February 26, 2026</p>
            <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-gray-200 p-4 inline-block">
              <p className="text-sm text-gray-700">
                <strong>By downloading, installing, or using Typeit 4U, you agree to be bound by these Terms & Conditions.</strong><br/>
                If you do not agree, please do not use the app.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20">
        <div className="container max-w-4xl">
          <div className="space-y-8">
            
            {/* Section 1 */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="bg-white rounded-2xl p-8 border border-gray-200"
            >
              <div className="flex items-start gap-4">
                <Building2 className="w-8 h-8 flex-shrink-0" style={{ color: '#FF6B9D' }} />
                <div>
                  <h2 className="text-2xl font-semibold mb-4" style={{ color: '#0A0A0A' }}>
                    1. About the App & Company
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Typeit 4U is an AI-powered keyboard application for Android, developed and owned by <strong>Deyvainfotech</strong>, a company based in Chennai, India. Typeit 4U is a product of Deyvainfotech.
                  </p>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                    <p><strong>Company:</strong> Deyvainfotech</p>
                    <p><strong>Product:</strong> Typeit 4U</p>
                    <p><strong>Platform:</strong> Android</p>
                    <p><strong>Contact:</strong> support@deyvainfotech.com</p>
                    <p><strong>Website:</strong> typeit4u.com</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Section 2 */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="bg-white rounded-2xl p-8 border border-gray-200"
            >
              <h2 className="text-2xl font-semibold mb-4" style={{ color: '#0A0A0A' }}>
                2. Acceptance of Terms
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                By downloading, installing, accessing, or using the Typeit 4U application or website, you confirm that:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-orange-500 mt-1">•</span>
                  <span>You are at least 13 years of age</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-orange-500 mt-1">•</span>
                  <span>You have read and understood these Terms & Conditions</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-orange-500 mt-1">•</span>
                  <span>You agree to comply with these terms and all applicable laws</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-orange-500 mt-1">•</span>
                  <span>If you are using the app on behalf of an organisation, you have the authority to bind that organisation to these terms</span>
                </li>
              </ul>
            </motion.div>

            {/* Section 3 */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="bg-white rounded-2xl p-8 border border-gray-200"
            >
              <h2 className="text-2xl font-semibold mb-4" style={{ color: '#0A0A0A' }}>
                3. Licence to Use
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Deyvainfotech grants you a personal, non-exclusive, non-transferable, revocable licence to:
              </p>
              <ul className="space-y-2 text-gray-700 mb-6">
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-1">✓</span>
                  <span>Download and install Typeit 4U on your Android device</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-1">✓</span>
                  <span>Use the app for your own personal or professional communication purposes</span>
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed mb-4 font-semibold">
                You may NOT:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">✗</span>
                  <span>Copy, modify, distribute, sell, or sublicence the app or any part of it</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">✗</span>
                  <span>Reverse engineer, decompile, or attempt to extract the source code of the app</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">✗</span>
                  <span>Use the app for any unlawful purpose or in violation of any applicable law</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">✗</span>
                  <span>Use automated tools or scripts to interact with the app</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">✗</span>
                  <span>Attempt to gain unauthorised access to any part of the app or its infrastructure</span>
                </li>
              </ul>
            </motion.div>

            {/* Section 4 - Free & Pro Plan */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="bg-gradient-to-br from-orange-50 to-pink-50 rounded-2xl p-8 border border-orange-200"
            >
              <h2 className="text-2xl font-semibold mb-6" style={{ color: '#0A0A0A' }}>
                4. Free Plan & Pro Plan
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Typeit 4U offers two tiers of service:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl p-6">
                  <h3 className="text-xl font-semibold mb-4" style={{ color: '#0A0A0A' }}>
                    Free Plan
                  </h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li>• Available to all users at no cost</li>
                    <li>• Includes 15 AI requests per day and access to core features</li>
                    <li>• Daily limits reset every 24 hours</li>
                    <li>• Free plan features may be updated or changed at any time with reasonable notice</li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl p-6 border-2 border-orange-300">
                  <h3 className="text-xl font-semibold mb-4" style={{ color: '#0A0A0A' }}>
                    Pro Plan
                  </h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li>• Available at ₹99/month or annual pricing, billed through Google Play</li>
                    <li>• Includes unlimited AI requests and access to all features</li>
                    <li>• 1-day free trial available for new subscribers</li>
                    <li>• Subscription renews automatically unless cancelled before the renewal date</li>
                    <li>• To cancel, go to Google Play Store → Profile → Subscriptions → Typeit 4U → Cancel</li>
                    <li>• No refunds are provided for partial billing periods unless required by law</li>
                  </ul>
                </div>
              </div>
            </motion.div>

            {/* Section 5 - AI Features */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="bg-white rounded-2xl p-8 border border-gray-200"
            >
              <h2 className="text-2xl font-semibold mb-4" style={{ color: '#0A0A0A' }}>
                5. AI Features & Third-Party Services
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Typeit 4U uses artificial intelligence features powered by <strong>OpenAI's API</strong>. By using AI features in Typeit 4U, you acknowledge and agree that:
              </p>
              <ul className="space-y-3 text-gray-700">
                <li>• AI features (Rephrase, Improve, Translate, Auto Reply, Caption Generator, Email Reply, etc.) require you to manually paste text — the app never reads your keyboard input automatically</li>
                <li>• Text you paste into AI features is transmitted securely to OpenAI's servers via HTTPS for processing</li>
                <li>• This text input is processed in real-time and is not stored permanently by Deyvainfotech or OpenAI for model training</li>
                <li>• AI-generated content is provided as a suggestion only — you are responsible for reviewing and approving all content before sending</li>
                <li>• Deyvainfotech does not guarantee the accuracy, appropriateness, or quality of AI-generated responses</li>
                <li>• You must not use AI features to generate content that is harmful, illegal, misleading, abusive, or in violation of any platform's terms of service</li>
              </ul>
            </motion.div>

            {/* Continue with remaining sections... Due to length, I'll add the key sections */}
            
            {/* Section 6 - OCR */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="bg-white rounded-2xl p-8 border border-gray-200"
            >
              <h2 className="text-2xl font-semibold mb-4" style={{ color: '#0A0A0A' }}>
                6. OCR Feature
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                The OCR (text scanning) feature uses Google ML Kit, which processes all images locally on your device. No image data is transmitted to Deyvainfotech's servers or any third party. By using the OCR feature, you confirm that:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li>• You have the right to scan and copy the text from the material you are scanning</li>
                <li>• You will not use the OCR feature to copy copyrighted material without permission</li>
                <li>• You will not use extracted text for fraudulent, illegal, or harmful purposes</li>
              </ul>
            </motion.div>

            {/* Section 7 - User Responsibilities */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="bg-white rounded-2xl p-8 border border-gray-200"
            >
              <h2 className="text-2xl font-semibold mb-4" style={{ color: '#0A0A0A' }}>
                7. User Responsibilities
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                You are solely responsible for all content you create, send, or share using Typeit 4U. You agree not to use Typeit 4U to:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li>• Send spam, unsolicited messages, or bulk communications</li>
                <li>• Harass, threaten, or harm any individual or group</li>
                <li>• Generate or distribute misinformation, fake news, or fraudulent content</li>
                <li>• Violate any applicable laws, including laws of the Republic of India</li>
                <li>• Infringe the intellectual property rights of any third party</li>
                <li>• Impersonate any person or organisation</li>
              </ul>
            </motion.div>

            {/* Section 8 - Intellectual Property */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="bg-white rounded-2xl p-8 border border-gray-200"
            >
              <h2 className="text-2xl font-semibold mb-4" style={{ color: '#0A0A0A' }}>
                8. Intellectual Property
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                All intellectual property rights in Typeit 4U — including but not limited to the app code, design, features, branding, name, and logo — are owned exclusively by Deyvainfotech. Nothing in these terms grants you any ownership or intellectual property rights in the app.
              </p>
              <p className="text-gray-700 leading-relaxed">
                The name 'Typeit 4U', 'Deyvainfotech', and associated logos are the property of Deyvainfotech. You may not use these names or logos for any commercial purpose without prior written permission.
              </p>
            </motion.div>

            {/* Section 9 - Disclaimers */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="bg-white rounded-2xl p-8 border border-gray-200"
            >
              <h2 className="text-2xl font-semibold mb-4" style={{ color: '#0A0A0A' }}>
                9. Disclaimers
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Typeit 4U is provided 'as is' and 'as available' without warranties of any kind. Deyvainfotech does not warrant that:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li>• The app will be uninterrupted, error-free, or free from bugs or viruses</li>
                <li>• AI-generated content will always be accurate, appropriate, or suitable for your purpose</li>
                <li>• The app will be compatible with all Android devices or operating system versions</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                You use Typeit 4U at your own risk. Deyvainfotech is not responsible for any content you create or send using the app.
              </p>
            </motion.div>

            {/* Section 10 - Limitation of Liability */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="bg-white rounded-2xl p-8 border border-gray-200"
            >
              <h2 className="text-2xl font-semibold mb-4" style={{ color: '#0A0A0A' }}>
                10. Limitation of Liability
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                To the maximum extent permitted by applicable law, Deyvainfotech and its founders, directors, and employees shall not be liable for:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li>• Any indirect, incidental, special, or consequential damages arising from your use of Typeit 4U</li>
                <li>• Loss of data, revenue, profits, or business arising from use or inability to use the app</li>
                <li>• Any harm resulting from AI-generated content you choose to send or publish</li>
                <li>• Any third-party claims arising from your use of the app</li>
              </ul>
            </motion.div>

            {/* Section 11-14 condensed for space */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="space-y-6"
            >
              <div className="bg-white rounded-2xl p-8 border border-gray-200">
                <h2 className="text-2xl font-semibold mb-4" style={{ color: '#0A0A0A' }}>11. Termination</h2>
                <p className="text-gray-700 leading-relaxed">
                  Deyvainfotech reserves the right to suspend or terminate your access to Typeit 4U at any time if you violate these Terms. Upon termination, your licence to use the app is revoked immediately. Subscription fees already paid are non-refundable unless required by law.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 border border-gray-200">
                <h2 className="text-2xl font-semibold mb-4" style={{ color: '#0A0A0A' }}>12. Changes to These Terms</h2>
                <p className="text-gray-700 leading-relaxed">
                  Deyvainfotech may update these Terms & Conditions from time to time. We will update the 'Last Updated' date and notify users of significant changes. Your continued use after changes constitutes acceptance of the revised terms.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 border border-gray-200">
                <h2 className="text-2xl font-semibold mb-4" style={{ color: '#0A0A0A' }}>13. Governing Law</h2>
                <p className="text-gray-700 leading-relaxed">
                  These Terms & Conditions are governed by the laws of the Republic of India. Any disputes shall be subject to the exclusive jurisdiction of the courts of Chennai, Tamil Nadu, India.
                </p>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-pink-50 rounded-2xl p-8 border border-orange-200">
                <h2 className="text-2xl font-semibold mb-4" style={{ color: '#0A0A0A' }}>
                  14. Contact Us
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  If you have any questions, concerns, or complaints about these Terms & Conditions, please contact us:
                </p>
                <div className="bg-white rounded-lg p-6 space-y-2">
                  <p className="text-gray-700"><strong>Company:</strong> Deyvainfotech</p>
                  <p className="text-gray-700"><strong>Product:</strong> Typeit 4U</p>
                  <p className="text-gray-700"><strong>Email:</strong> support@deyvainfotech.com</p>
                  <p className="text-gray-700"><strong>Website:</strong> typeit4u.com</p>
                  <p className="text-gray-700"><strong>Location:</strong> Chennai, Tamil Nadu, India</p>
                </div>
              </div>
            </motion.div>

            {/* Acceptance Box */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="bg-white rounded-2xl p-8 border-2 border-green-200 text-center"
            >
              <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-600" />
              <h3 className="text-2xl font-semibold mb-4" style={{ color: '#0A0A0A' }}>
                By Using Typeit 4U
              </h3>
              <p className="text-gray-700 leading-relaxed">
                You acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
              </p>
              <p className="text-sm text-gray-600 mt-6">
                Typeit 4U — Type Less. Do More. | A Deyvainfotech Product | Chennai, India 🇮🇳
              </p>
            </motion.div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsConditions;
